import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronLeft, ExternalLink, Radar } from "lucide-react";
import { api, type RadarMomentView } from "../../lib/api.js";
import type { BackfillPreset } from "./BackfillDialog.js";

/**
 * WHAT IS COMING, AND WHAT WE HAVE FOR IT.
 *
 * The seasonal moments of the next four months (after the holidays, the clock
 * change, Hanukkah…), each with the live articles worth re-sharing around it
 * and the backlog keywords a new article could take. Suggestions only: a
 * keyword opens in the pipeline, where the duplicate gate and the
 * operator-owned guard still decide; an article opens the calendar fill,
 * pre-set to the moment's date.
 *
 * The phase says which of the two is still possible. A new article needs about
 * six weeks to be written and to rank, so inside that window the useful move
 * is to share what already exists.
 */

const fmt = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString("he-IL", { day: "numeric", month: "numeric" });

const PHASE: Record<RadarMomentView["phase"], { label: string; chip: string }> = {
  write: { label: "זמן לכתוב מאמר חדש", chip: "bg-emerald-100 text-emerald-900" },
  reshare: { label: "מאוחר למאמר חדש: לשתף קיים", chip: "bg-amber-100 text-amber-900" },
  now: { label: "עכשיו", chip: "bg-sky-100 text-sky-900" },
};

export function ContentRadar({ onReshare }: { onReshare: (preset: BackfillPreset) => void }) {
  const radar = useQuery({ queryKey: ["content-radar"], queryFn: () => api.contentRadar(120), staleTime: 5 * 60_000 });
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const moments = radar.data?.moments ?? [];
  // Holiday greetings are drafted by themselves 5 days ahead; "now" drafts one early.
  const greetings = useQuery({ queryKey: ["social-greetings"], queryFn: () => api.socialGreetings(), staleTime: 5 * 60_000 });
  const createGreeting = useMutation({
    mutationFn: (key: string) => api.createGreeting(key),
    onSettled: () => void greetings.refetch(),
  });
  if (radar.isError || (radar.data && moments.length === 0)) return null;

  return (
    <section className="rounded-lg border border-line bg-white" dir="rtl">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-3 py-2 text-right text-xs">
        {open ? <ChevronDown size={14} /> : <ChevronLeft size={14} />}
        <Radar size={14} />
        <strong>מועדים קרובים</strong>
        <span className="flex flex-wrap gap-1.5">
          {moments.slice(0, 5).map((m) => (
            <span key={`${m.key}-${m.from}`} className={`rounded px-1.5 py-0.5 ${PHASE[m.phase].chip}`}>
              {m.labelHe} · {m.daysUntil === 0 ? "עכשיו" : `בעוד ${m.daysUntil} ימים`}
            </span>
          ))}
        </span>
      </button>

      {open && (
        <div className="space-y-2 border-t border-line p-3">
          <p className="text-[11px] text-muted">
            הצעות בלבד. מאמר חדש צריך כ־{Math.round((radar.data?.writeLeadDays ?? 42) / 7)} שבועות כדי להיכתב ולדרג, ולכן קרוב למועד עדיף לשתף מאמר קיים.
            מילת מפתח נפתחת בפייפליין, ושם עדיין רצות בדיקות הכפילות והשיוך לעמודי טיפול.
          </p>
          {(greetings.data?.occasions.length ?? 0) > 0 && (
            <div className="rounded-lg border border-line px-3 py-2 text-[11px]">
              <p className="mb-1 font-medium">ברכות חג (טיוטה נוצרת לבד {greetings.data!.leadDays} ימים לפני, לאישור שלך)</p>
              <ul className="space-y-0.5">
                {greetings.data!.occasions.map((g) => (
                  <li key={`${g.key}-${g.holidayDate}`} className="flex flex-wrap items-center gap-2">
                    <span>{g.labelHe}</span>
                    <span className="text-muted">
                      {new Date(g.postAtIso).toLocaleString("he-IL", { timeZone: "Asia/Jerusalem", weekday: "short", day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })}
                      {" · "}{g.tier === 1 ? "פיד + סטורי" : "סטורי בלבד"}
                    </span>
                    {g.packId ? (
                      <span className="text-emerald-800">טיוטה ביומן ✓</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => createGreeting.mutate(g.key)}
                        disabled={createGreeting.isPending}
                        className="rounded border border-line px-1.5 py-px hover:bg-canvas disabled:opacity-40"
                        title="תמונה אחת + כיתוב קצר, כ־$0.05"
                      >{createGreeting.isPending && createGreeting.variables === g.key ? "יוצר…" : "ליצור טיוטה עכשיו"}</button>
                    )}
                  </li>
                ))}
              </ul>
              {createGreeting.data && !createGreeting.data.ok && <p className="mt-1 text-danger">{createGreeting.data.error}</p>}
            </div>
          )}

          {moments.map((m) => {
            const id = `${m.key}-${m.from}`;
            const isOpen = expanded === id;
            return (
              <div key={id} className="rounded-lg border border-line">
                <button type="button" onClick={() => setExpanded(isOpen ? null : id)} className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-right text-xs">
                  {isOpen ? <ChevronDown size={13} /> : <ChevronLeft size={13} />}
                  <strong>{m.labelHe}</strong>
                  <span className="text-muted">{fmt(m.from)}{m.to !== m.from ? `–${fmt(m.to)}` : ""}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[10px] ${PHASE[m.phase].chip}`}>{PHASE[m.phase].label}</span>
                  {m.phase === "write" && <span className="text-[10px] text-muted">להתחיל עד {fmt(m.writeBy)}</span>}
                  <span className="ms-auto text-[10px] text-muted">{m.articles.length} מאמרים · {m.keywords.length} מילות מפתח</span>
                </button>

                {isOpen && (
                  <div className="grid gap-3 border-t border-line p-3 text-[11px] md:grid-cols-3">
                    <div>
                      <p className="mb-1 font-medium">זוויות</p>
                      <ul className="list-disc space-y-0.5 ps-4">{m.angles.map((a) => <li key={a}>{a}</li>)}</ul>
                    </div>

                    <div>
                      <p className="mb-1 font-medium">מאמרים חיים לשתף סביב המועד</p>
                      {m.articles.length === 0 && <p className="text-muted">אין מאמר חי שמתאים.</p>}
                      <ul className="space-y-1">
                        {m.articles.map((a) => (
                          <li key={a.backfillId} className="flex items-start gap-1">
                            <span className="grow">
                              {a.title}
                              {a.impressions28d ? <span className="text-muted"> · {a.impressions28d.toLocaleString("he-IL")} חשיפות</span> : null}
                            </span>
                            {a.url && <a href={a.url} target="_blank" rel="noreferrer" className="shrink-0 text-muted hover:text-ink" title="לעמוד"><ExternalLink size={11} /></a>}
                            <button
                              type="button"
                              onClick={() => onReshare({
                                source: a.backfillSource,
                                ids: [a.backfillId],
                                startIso: `${m.from}T06:00:00.000Z`,
                                note: `שיתוף סביב "${m.labelHe}" (${fmt(m.from)}): המילוי יחפש זמנים פנויים החל מתחילת המועד. אפשר להוסיף מאמרים נוספים מהרשימה.`,
                              })}
                              className="shrink-0 rounded border border-line px-1.5 py-px hover:bg-canvas"
                            >לשתף</button>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <p className="mb-1 font-medium">מילות מפתח לבלוג (בלי עמוד קיים)</p>
                      {m.keywords.length === 0 && <p className="text-muted">אין מילת מפתח מתאימה בבקלוג.</p>}
                      <ul className="space-y-0.5">
                        {m.keywords.map((k) => (
                          <li key={k.id}>
                            <a href={`#/pipeline?open=${encodeURIComponent(k.id)}`} className="text-brand-700 hover:underline">{k.keyword}</a>
                            <span className="text-muted"> · {k.volume ?? "?"}</span>
                          </li>
                        ))}
                      </ul>
                      {m.gap && (
                        <p className="mt-1.5 rounded bg-amber-50 px-2 py-1 text-amber-900">
                          פער: אין בבקלוג מילה עם נפח משמעותי לזוויות של המועד. כדאי לחקור מילות מפתח לזוויות למעלה.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
